import type { VacationBalance } from '../services/api/timeOff';

/**
 * MKS-326 — converting between the two hour scales the time-off feature uses.
 *
 * `TimeOff.totalHours`, `dateFromHours` and `dateToHours` are stored on a
 * fraction-of-standard-day scale where **8 means one whole working day**,
 * whatever that day is worth for the person. (Capacity planning reads
 * `hours < 8` as a partial day and the per-day cost trigger multiplies by it,
 * which is why the scale is not the person's real hours.)
 *
 * The vacation balance, on the other hand, is in REAL hours: it already draws
 * `storedHours × FTE` per day. `balance.dailyWorkingHours` (= 8 × FTE) is the
 * bridge between the two, and the number the employee actually recognises as
 * "my working day".
 */

/** Hours in one whole working day at 1.0 FTE — the unit the stored figures use. */
export const STANDARD_DAY_HOURS = 8;

/** A balance whose figures are all present — i.e. the person has an entitlement. */
export interface ResolvedVacationBalance extends VacationBalance {
  entitlementHours: number;
  usedHours: number;
  pendingHours: number;
  carriedInHours: number;
  remainingHours: number;
  dailyWorkingHours: number;
}

/**
 * `true` when the person has a vacation entitlement at all.
 *
 * Contractors and freelancers get every field back as `null` (VAC-04 "no limit
 * exists", not "a limit of zero"), and a `dailyWorkingHours` of 0 would make
 * every day conversion a division by zero — so both are excluded here, and the
 * callers then show nothing and check nothing.
 */
export function hasVacationEntitlement(
  balance: VacationBalance | null | undefined
): balance is ResolvedVacationBalance {
  return (
    balance != null &&
    balance.entitlementHours != null &&
    balance.usedHours != null &&
    balance.pendingHours != null &&
    balance.carriedInHours != null &&
    balance.remainingHours != null &&
    balance.dailyWorkingHours != null &&
    balance.dailyWorkingHours > 0
  );
}

/**
 * Stored fraction-of-standard-day hours → the person's REAL hours.
 * At a 4 h day (0.5 FTE), a stored full day of `8` is 4 real hours.
 */
export function storedHoursToReal(storedHours: number, dailyWorkingHours: number): number {
  return (storedHours * dailyWorkingHours) / STANDARD_DAY_HOURS;
}

/** Real hours → whole working days of the person's own length. */
export function hoursToOwnDays(hours: number, dailyWorkingHours: number): number {
  return dailyWorkingHours > 0 ? hours / dailyWorkingHours : 0;
}

/** Hours with at most one decimal, trailing `.0` trimmed: `20`, `6.4`, `-2.5`. */
export function formatBalanceHours(hours: number): string {
  if (!Number.isFinite(hours)) return '—';
  return `${parseFloat(hours.toFixed(1))}`;
}

/** Days with at most one decimal, trailing `.0` trimmed: `5`, `2.5`. */
export function formatBalanceDays(days: number): string {
  if (!Number.isFinite(days)) return '—';
  return `${parseFloat(days.toFixed(1))}`;
}

/** `5 days (20 h)` — the person's own days, and the hours behind them. */
export function formatDaysAndHours(hours: number, dailyWorkingHours: number): string {
  const days = hoursToOwnDays(hours, dailyWorkingHours);
  const dayWord = Math.abs(parseFloat(days.toFixed(1))) === 1 ? 'day' : 'days';
  return `${formatBalanceDays(days)} ${dayWord} (${formatBalanceHours(hours)} h)`;
}

/**
 * The stored-scale hour total of a request, from the same inputs the backend
 * uses: first day `dateFromHours`, last day `dateToHours`, every day between
 * them a whole `8`. A single-day request takes the smaller of the two, exactly
 * as `timeOffController.formatTimeOff` does.
 */
export function requestStoredHours(
  workingDays: number,
  dateFromHours: number,
  dateToHours: number
): number {
  if (workingDays <= 0) return 0;
  if (workingDays === 1) return Math.min(dateFromHours, dateToHours);
  return dateFromHours + (workingDays - 2) * STANDARD_DAY_HOURS + dateToHours;
}
